import type {
  SubmissionAssignmentDetail,
  SubmissionModel,
} from "@/types/submission.model";

export function isSubmissionAssignmentObject(
  assignment: SubmissionModel["assignment"]
): assignment is SubmissionAssignmentDetail {
  return typeof assignment === "object" && assignment !== null;
}

export function getSubmissionAssignType(
  assignment: SubmissionModel["assignment"]
): "GENERAL" | "UNIT" | undefined {
  if (!isSubmissionAssignmentObject(assignment)) {
    return undefined;
  }

  return assignment.assign_type ?? assignment.assignment_type;
}

export function getSubmissionStatusLabel(
  status: SubmissionModel["status"]
): string {
  if (typeof status === "string") {
    return status;
  }

  return status?.str ?? "";
}

export function getSubmissionResourceName(record: SubmissionModel): string {
  if (record.type === "QUERY") {
    return record.grade?.name ?? "";
  }

  if (!isSubmissionAssignmentObject(record.assignment)) {
    return record.grade?.name ?? "";
  }

  return (
    record.assignment.grade?.name ??
    record.assignment.class_offering?.name ??
    record.grade?.name ??
    ""
  );
}

export function isSubmissionClassRecord(record: SubmissionModel): boolean {
  if (record.type === "QUERY") {
    return false;
  }

  if (!isSubmissionAssignmentObject(record.assignment)) {
    return false;
  }

  const { assignment } = record;

  return (
    assignment.type === "class" ||
    Boolean(assignment.class_offering?.id && !assignment.grade?.id)
  );
}

export function getSubmissionResourceId(
  record: SubmissionModel
): string | undefined {
  if (record.type === "QUERY") {
    return record.grade?.id;
  }

  if (!isSubmissionAssignmentObject(record.assignment)) {
    return undefined;
  }

  const { assignment } = record;

  if (isSubmissionClassRecord(record)) {
    return assignment.class_offering?.id;
  }

  return assignment.grade?.id ?? undefined;
}

export function getSubmissionViewPath(record: SubmissionModel): string | null {
  const resourceId = getSubmissionResourceId(record);

  if (!resourceId) {
    return null;
  }

  if (isSubmissionClassRecord(record)) {
    return `/class-management/detail/${resourceId}`;
  }

  return `/course-management/view-course/${resourceId}`;
}
